import React, { useEffect, useState } from "react";
import { proxyAPI } from "../proxyAPI";
import TemplateForm from "./TemplateForm";
import { Link } from "react-router-dom";
import ContactsIcon from "../../assets/icons/contact_edit.svg2";
import { shortUUID } from "../helpers";
import MyPagination from "../Common/MyPagination";
import HelpButton from "../HelpContent/HelpButton";
import { useConfirmDialog } from "../Builder/Components/ConfirmDialogContext";

export interface TemplateType {
  uuid: string;
  label: string;
  type: string;
  fields: any[];
  isNew?: boolean;
}

export default function ReportingTemplates() {
  const confirmDialog = useConfirmDialog();
  const [templates, setTemplates] = useState<TemplateType[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const getTemplates = async (page = 0, perPage = 12) => {
    const data = await proxyAPI({
      prefix: "logic/reporting",
      url: `templates?pagination.perPage=${perPage}&pagination.page=${page}`,
    });
    if (data?.data) {
      setTemplates(data.data);
      setTotal(data.info.total);
    }
  };

  const handleAddTemplate = async () => {
    setTemplates((p) => [
      {
        uuid: shortUUID(),
        label: "new template",
        type: "STACK",
        isNew: true,
        fields: [
          {
            uuid: shortUUID(),
            label: "field",
            defaultValue: "default",
            order: 0,
            type: "TEXT",
            categories: [],
          },
        ],
      },
      ...p,
    ]);
  };

  const handleDelete = async (template: TemplateType) => {
    if (
      !(await confirmDialog(
        "Delete template",
        "Are you sure you want to delete template?"
      ))
    ) {
      return;
    }
    if (template.isNew) {
      setTemplates((p) => p.filter((t) => t.uuid !== template.uuid));
    } else {
      const data = await proxyAPI({
        prefix: "logic/reporting",
        url: "templates",
        method: "DELETE",
        body: { uuids: [template.uuid] },
        setLoading,
      });
      if (data) {
        getTemplates();
      }
    }
  };

  useEffect(() => {
    getTemplates();
  }, []);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center p-4">
        <div className="d-flex align-items-start">
          <ContactsIcon width={30} className="mt-2 me-3" />
          <div className="">
            <h1 className="fw-bold mb-1">
              Reporting Templates
              <HelpButton helpKey="reporting-templates" />
            </h1>
            <span className="opacity-50">
              Create and customise templates to standardise your reports.
            </span>
          </div>
        </div>
        <div className="d-flex">
          <Link
            className="btn d-flex align-items-center bg-white rounded-3 py-2 ps-3 pe-5 me-3"
            to="/reporting/topics"
          >
            <ContactsIcon width={20} />
            <div className="border-end border-tertiary mx-2 align-self-stretch" />
            <div className="text-start small">
              <h6 className="fw-bold mb-0">
                Topics <HelpButton helpKey="topics-reports" />
              </h6>
              <span className="small opacity-50">Manage</span>
            </div>
          </Link>
        </div>
      </div>
      <div className="p-4 my-2 rounded-3">
        <div className="text-end mb-2">
          <button
            type="button"
            className="btn btn-colored"
            onClick={handleAddTemplate}
          >
            Add template
          </button>
        </div>
        {templates.map((template, index) => (
          <div key={template.uuid} className="mb-3">
            <TemplateForm template={template} handleDelete={handleDelete} />
            {index < templates.length - 1 && (
              <hr className="border-top opacity-100 border-3 my-3" />
            )}
          </div>
        ))}
      </div>
      <MyPagination onPaginate={getTemplates} total={total} />
    </div>
  );
}
